import styled, { css } from 'styled-components';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  DatePickerField,
  TypeaheadTimePickerField,
  TimeZonePickerField,
  BasicSelectField,
} from 'lib/iterable-hook-form/FormField';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { colors, spacing, Subtitle2 } from 'ui/theme';
import { HelpToggletip, TipPlacement } from 'ui/tooltip';
import { ALIGNMENT } from 'ui/selects';
import { ValueDisplay } from 'ui/selects/SelectValueDisplay.styles';
import { getIsJourneyStartTimeOptimizationEnabled } from 'modules/workflows/ducks';
import { useStartNodeContext } from '../../StartNodeEditor.context';
import { SCHEDULE_DATE_TEST_ID } from './Scheduler.constants';
import { RowWrapper } from './SchedulerForm.styled';
import { getMinStartDate } from './Scheduler.utils';
import { getTimeOffsetOptions } from './StartDateAndTime.utils';

const StyledHeader = styled(Subtitle2)`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

const StyledTimeOffsetSelect = styled(BasicSelectField)<{ selectedTimeOffset: number }>`
  ${({ selectedTimeOffset }) =>
    selectedTimeOffset > 0 &&
    css`
      ${ValueDisplay} {
        ::before {
          content: 'within';
          color: ${colors.secondaryText};
          margin-right: ${spacing.quarter};
        }
      }
    `}
`;

export const StartDateAndTime = () => {
  const isStartTimeOptEnabled = useSelector(getIsJourneyStartTimeOptimizationEnabled);
  const { isReadOnly } = useStartNodeContext();
  const [startDate, minuteOfDay, timeZone, maxStartTimeOffsetInMinutes] = useWatch({
    name: ['startDate', 'minuteOfDay', 'timeZone', 'maxStartTimeOffsetInMinutes'],
  });

  const { defaultValues } = useFormState();
  const { trigger } = useFormContext();

  useEffect(() => {
    // validate endDate anytime date/time changes
    trigger('endDate');
  }, [startDate, minuteOfDay, timeZone]);

  const minStart = useMemo(() => getMinStartDate(timeZone), [timeZone]);
  const timeOffsetOptions = useMemo(
    () => getTimeOffsetOptions(minuteOfDay || defaultValues?.minuteOfDay),
    [minuteOfDay, defaultValues],
  );

  return (
    <div data-test={SCHEDULE_DATE_TEST_ID}>
      {isStartTimeOptEnabled ? (
        <StyledHeader>
          Start date and time window{' '}
          <HelpToggletip
            placement={TipPlacement.Top}
            tooltipContent="Select a time frame for Iterable to start adding users to this journey. Selecting a window of 15-60 minutes (instead of an exact time) can help prevent slowdowns when multiple journeys are running simultaneously."
            iconProps={{ color: colors.secondaryText }}
          />
        </StyledHeader>
      ) : (
        <Subtitle2>Start date and time</Subtitle2>
      )}
      <RowWrapper>
        <DatePickerField
          name="startDate"
          validDateRange={{ start: minStart }}
          isDisabled={isReadOnly}
          isReadOnly={isReadOnly}
        />
        <TypeaheadTimePickerField
          name="minuteOfDay"
          increment={15}
          inputClockFormat="12-hour"
          isDisabled={isReadOnly}
        />
        <TimeZonePickerField name="timeZone" isDisabled={isReadOnly} />
        {isStartTimeOptEnabled && (
          <StyledTimeOffsetSelect
            name="maxStartTimeOffsetInMinutes"
            options={timeOffsetOptions}
            selectedTimeOffset={maxStartTimeOffsetInMinutes}
            horizontalAlignment={ALIGNMENT.RIGHT}
            isDisabled={isReadOnly}
            data-test="time-offset-id"
          />
        )}
      </RowWrapper>
    </div>
  );
};
